import {useCallback, useEffect, useRef} from 'react';
import isEqual from 'react-fast-compare';

import {AsyncState, AsyncStatus} from 'ui-library/hooks/useAsyncState';
import usePrevious from 'ui-library/hooks/usePrevious';

export function useAutoUpdatePreview<TPreviewRequestData, TPreviewResponseData>(
  requestData: TPreviewRequestData | null,
  generatePreview: (
    requestData: TPreviewRequestData,
    setAsyncState: (nextState: AsyncState<TPreviewResponseData, [TPreviewRequestData]>) => void
  ) => void,
  onPreviewUpdate: (previewState: AsyncState<TPreviewResponseData, [TPreviewRequestData]>) => void
): void {
  const previousRequestData = usePrevious(requestData);
  // Create mutable ref so that, once a request callback is called, we can compare the request data at the
  // time the request was made (`requestData`) against the current request data (`requestDataRef.current`).
  const pendingRequestDataRef = useRef<TPreviewRequestData | null>(requestData);

  const requestGeneratePreview = useCallback(
    (requestData: TPreviewRequestData | null) => {
      if (requestData == null) {
        onPreviewUpdate({status: AsyncStatus.NOT_FETCHED});
      } else {
        generatePreview(requestData, (nextState) => {
          // Only trigger callback if the used request data (`requestData`) matches the current request data.
          if (isEqual(pendingRequestDataRef.current, requestData)) {
            onPreviewUpdate(nextState);
          }
        });
      }
    },
    [generatePreview, onPreviewUpdate]
  );

  useEffect(() => {
    if (!isEqual(requestData, previousRequestData)) {
      pendingRequestDataRef.current = requestData;
      requestGeneratePreview(requestData);
    }
  }, [requestData, previousRequestData, requestGeneratePreview]);
}
